import {
  type FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ArrowTopIcon from '@assets/img/icons/arrow-top.svg?react';
import ChevronIcon from '@assets/img/icons/chevron.svg?react';
import VipIcon from '@assets/img/icons/vip-crown.svg?react';
import { Capacitor } from '@capacitor/core';
import cn from 'classnames';
import { motion, useAnimate } from 'framer-motion';

import { useLazyGetLotteryInfoQuery } from '@/api/queries/lottery/lottery';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import {
  type ProductOfDayDataType,
  type ProductOfDayRewardItem,
} from '@/common/types/contract';
import ProductOfDaySpinReward from '@/components/ProductOfDaySpinReward/ProductOfDaySpinReward';
import { TextSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import UpgradeGreenArrowsAnimation from '@/components/ui/UpgradeGreenArrowsAnimation/UpgradeGreenArrowsAnimation';
import { selectProductOfDayById } from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import ProductOfDaySpinRewardModal from './ProductOfDaySpinReward';
import ProductOfDaySpinWrapper from './ProductOfDaySpinWrapper';

import styles from './styles.module.scss';

import purpleWheelPng from '/img/contract/purplewheel.png';
import purpleWheelWebp from '/img/contract/purplewheel.webp';
import spinFrame from '/img/productOfDay/spin-frame.svg';

interface ProductOfDaySpinModalProps {
  productId: string;
}

const ProductOfDaySpinModal: FC<ProductOfDaySpinModalProps> = ({
  productId,
}) => {
  const vipIsActive = useCheckVIPstatus();
  const { openModal, secondModal } = useContext(ModalContext);
  const product = useAppSelector((state) =>
    selectProductOfDayById(state, productId),
  );
  const { t } = useTranslation();
  const { thirdModal } = useContext(ModalContext);
  const [stage, setStage] = useState<'spin' | 'claim'>('spin');
  const [isSpinning, setIsSpinning] = useState(false);
  const [getLotteryInfo] = useLazyGetLotteryInfoQuery();

  const [, animate] = useAnimate();

  const sortRewards = (
    rewards: ProductOfDayRewardItem[],
  ): ProductOfDayRewardItem[] => {
    const sortOrder = [0, 4, 6, 1, 3, 2, 5, 7];
    const result: ProductOfDayRewardItem[] = [];

    for (
      let newPos = 0;
      newPos < Math.min(sortOrder.length, rewards.length);
      newPos++
    ) {
      const originalPos = sortOrder[newPos];

      if (originalPos < rewards.length) {
        result[newPos] = rewards[originalPos];
      }
    }

    if (rewards.length > 8) {
      for (let i = 8; i < rewards.length; i++) {
        result[i] = rewards[i];
      }
    }

    const usedIndices = new Set();
    for (let i = 0; i < Math.min(sortOrder.length, rewards.length); i++) {
      if (sortOrder[i] < rewards.length) {
        usedIndices.add(sortOrder[i]);
      }
    }

    let nextPos = Math.min(sortOrder.length, rewards.length);
    for (let i = 0; i < rewards.length; i++) {
      if (!usedIndices.has(i)) {
        result[nextPos++] = rewards[i];
      }
    }

    return result.filter(Boolean);
  };

  const rewards = useMemo(
    () =>
      product?.rewardInfo[product.type]
        ? {
            ...product.rewardInfo[product.type],
            reward: sortRewards(product.rewardInfo[product.type].reward),
          }
        : undefined,
    [product],
  );

  const list1 = useRef<HTMLDivElement>(null);
  const list2 = useRef<HTMLDivElement>(null);

  const findRewardIndex = (
    rewardData: ProductOfDayDataType['chestboxReward'],
  ): number => {
    const originalRewards = rewards?.reward ?? [];
    const rewardName = rewardData ? Object.keys(rewardData[0])[0] : undefined;
    const rewardValue =
      rewardName && rewardData && rewardData[0]
        ? rewardData[0][rewardName as keyof (typeof rewardData)[0]]
        : undefined;

    const originalIndex = originalRewards.findIndex(
      (r) => r.type === rewardName && r.value === rewardValue,
    );

    return originalIndex;
  };

  const rewardIndex = findRewardIndex(product?.chestboxReward);

  const handleSpin = async (): Promise<void> => {
    if (isSpinning || !list1.current || !list2.current) return;

    setIsSpinning(true);

    const speeds = [
      0.07, 0.075, 0.08, 0.09, 0.0105, 0.12, 0.18, 0.25, 0.4, 0.6, 0.8, 1.2,
      1.7,
    ];

    for (let i = 0; i < speeds.length; i++) {
      list1.current.style.transform = '';
      list2.current.style.transform = '';
      await Promise.all([
        animate(
          list1.current,
          { x: ['0px', 'calc(-100% - 8px)'] },
          {
            duration: speeds[i],
            ease: 'linear',
          },
        ),
        animate(
          list2.current,
          { x: ['0px', 'calc(-100% - 8px)'] },
          {
            duration: speeds[i],
            ease: 'linear',
          },
        ),
      ]);
    }

    list1.current.style.transform = '';
    list2.current.style.transform = '';

    const count = rewards?.reward.length || 1;
    const stepPercent = (1 / count) * (rewardIndex - 1);

    const finalSpeed = 1.7 * ((rewardIndex + 1) / count);

    if (rewardIndex === 0) {
      const finalPosition = `calc(${-100 - stepPercent * 100}% - 14px)`;

      await Promise.all([
        animate(
          list1.current,
          { x: ['0px', finalPosition] },
          { duration: finalSpeed, ease: 'linear' },
        ),
        animate(
          list2.current,
          { x: ['0px', finalPosition] },
          { duration: finalSpeed, ease: 'linear' },
        ),
      ]);
    } else {
      const finalPosition = `calc(-${stepPercent * 100}% - ${8 * stepPercent}px)`;

      await Promise.all([
        animate(
          list1.current,
          { x: ['0px', finalPosition] },
          { duration: finalSpeed, ease: 'linear' },
        ),
        animate(
          list2.current,
          { x: ['0px', finalPosition] },
          { duration: finalSpeed, ease: 'linear' },
        ),
      ]);
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));
    setStage('claim');
    setIsSpinning(false);
  };

  useEffect(() => {
    if (!list1.current || !list2.current || isSpinning) return;

    const controls1 = animate(
      list1.current,
      { x: ['0px', 'calc(-100% - 8px)'] },
      {
        duration: 20,
        ease: 'linear',
        repeat: Infinity,
      },
    );

    const controls2 = animate(
      list2.current,
      { x: ['0px', 'calc(-100% - 8px)'] },
      {
        duration: 20,
        ease: 'linear',
        repeat: Infinity,
      },
    );

    return (): void => {
      controls1.cancel();
      controls2.cancel();
    };
  }, [isSpinning]);

  const getTitle = (): string => {
    switch (product?.type) {
      case 'regular':
        return t('Standard Spin');
      case 'premium':
        return t('Premium Spin');
      case 'vip':
        return t('VIP Spin');
      default:
        return t('Standard Spin');
    }
  };

  const isCompleted = product && product?.progress >= product?.countToReward;

  useEffect(() => {
    // to display lottery time later
    if (
      product?.chestboxReward?.find(
        (reward) =>
          !!reward.goldTicket ||
          !!reward.diamondTicket ||
          !!reward.silverTicket,
      )
    ) {
      getLotteryInfo().unwrap();
    }
  }, [product?.chestboxReward]);

  if (stage === 'claim' && product?.chestboxReward)
    return (
      <ProductOfDaySpinRewardModal
        productId={product!.id}
        chestBoxOpenedAt={product!.chestBoxOpenedAt}
        chestboxReward={product.chestboxReward}
      />
    );

  return (
    <ProductOfDaySpinWrapper
      title={getTitle()}
      subtitle={t('Daily Goods')}
      buttonText={isSpinning ? '...' : t('Spin')}
      onSpin={handleSpin}
      disabled={isSpinning}
      inactive={isSpinning}
      isCompleted={isCompleted}
    >
      <div
        className={cn(
          'relative flex-1 self-stretch overflow-hidden -mx-6',
          styles.mask,
        )}
      >
        {/* VIP only adds a USD chance here, and the native app has no USD */}
        {!vipIsActive && !Capacitor.isNativePlatform() && (
          <div
            onClick={() => {
              if (isSpinning) return;
              secondModal.closeModal();
              openModal({
                renderKind: 'selectVipPrice',
                props: { type: 'paid' },
              });
            }}
            className={styles.buyVip}
          >
            <VipIcon className="absolute left-0 top-0 size-[48px]" />
            <TextSmall weight="medium" className="leading-1.2">
              {t('The chance to receive USD rewards is available only for VIP')}
            </TextSmall>
            <ChevronIcon className="size-[24px] text-gold shrink-0" />
          </div>
        )}
        <img
          className="w-[184px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none z-20"
          src={spinFrame}
          alt="spin frame"
        />
        <div className={styles.wrapper}>
          <motion.div ref={list1} className="flex gap-[8px] flex-shrink-0">
            {rewards?.reward.map((reward, index) => (
              <ProductOfDaySpinReward
                key={`first-${index}`}
                type={reward.type}
                value={reward.value}
              />
            ))}
          </motion.div>

          <motion.div
            ref={list2}
            className="flex gap-[8px] flex-shrink-0 ml-[8px]"
          >
            {rewards?.reward.map((reward, index) => (
              <ProductOfDaySpinReward
                key={`second-${index}`}
                type={reward.type}
                value={reward.value}
              />
            ))}
          </motion.div>
        </div>

        {product && product.type !== 'premium' && !product.chestBoxOpenedAt && (
          <button
            className={styles.glassButton}
            disabled={isSpinning}
            onClick={() =>
              thirdModal.openModal({
                renderKind: 'productOfDayModal',
                props: { productId: product.id, initialScreen: 'upgrade' },
              })
            }
          >
            <ArrowTopIcon className="size-[18px] text-gold shrink-0 mr-[10px]" />
            <span className="text-nowrap">{t('Upgrade  rewards')}</span>
            <div className="absolute -right-2 size-[4.5rem] p-0.5">
              <UpgradeGreenArrowsAnimation className="w-[2.125rem] absolute right-0 bottom-7 -space-y-1 z-[-1]" />
              <Picture
                className="size-full"
                img={purpleWheelPng}
                webp={purpleWheelWebp}
                alt="green box"
              />
            </div>
          </button>
        )}
      </div>
    </ProductOfDaySpinWrapper>
  );
};

export default ProductOfDaySpinModal;
